/* The composer: the P5 boards' pill (owner, 4 November 2026; `BuddyP5f-1-start`, `BuddyP5d-3-rozmowa`,
 * `BuddyP5-3-stany`), the same in the hero and the dock, with the builder panel's own box beside
 * it; the controls and the tray are shared. The raised pill keeps its 3D (the owner's ask); the
 * round send is flat (`--primary-fill`, no shadow) and so is everything else. See `surfaces.module.css` for the token families. */

.composer {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* The builder's assistant (boards I, S): the panel's own width, a little less room under the tray. */
.panel {
  gap: var(--space-2);
}

/* The pill: „+ · text · language · mic · send" in one 56 px row. Every child names its cell, so the
 * recording lane can lie over the first ones without pushing the others round. */
.box {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-areas: "sheet field language mic action";
  grid-template-columns: auto minmax(0, 1fr) auto auto auto;
  column-gap: var(--space-2px);
  align-items: center;
  min-height: 56px;
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-10px);
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-buddy-composer);
  transition-duration: var(--dur-slow);
  transition-property: transform, box-shadow, border-color;
  transition-timing-function: var(--ease);
}

/* Writing (P5 „Pisanie"): past one line the text takes the width and the controls step down. */
.multi {
  grid-template-areas:
    "field field field field field"
    "sheet . . mic action";
  row-gap: var(--space-10px);
  padding: var(--space-4) var(--space-2) var(--space-2) var(--space-10px);
  border-radius: var(--radius-xl);
}

/* The panel keeps its own box: the field over the send, at the board's corner. */
.panel .box {
  grid-template-areas: none;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-10px);
  align-items: end;
  padding: var(--space-14px) var(--space-3) var(--space-3) var(--space-18px);
  border-radius: var(--radius-23px);
}

/* Focus lifts the box 2px under a deeper shadow; the edge keeps its resting hairline (owner,
 * 4 November 2026: a dark focus edge read as a needless frame round the pill). */
.box:focus-within {
  box-shadow: var(--shadow-buddy-composer-lifted);
  transform: translateY(-2px);
}

.boxOff,
.boxOff:focus-within {
  box-shadow: var(--shadow-buddy-composer-off);
  transform: none;
}

.field {
  width: 100%;
  margin: var(--space-0);
  padding: var(--space-0);
  font-family: var(--font-body);
  line-height: var(--lh-lede);
  color: var(--text);
  resize: none;
  outline: none;
  background: transparent;
  border: 0;

  /* Grows with the prompt (`useAutoGrow`) up to this height, then scrolls inside the box. */
  max-height: min(320px, 45vh);
  overflow-y: auto;
}

/* The secondary ink: the tertiary one measured 3.0:1 on the light face. */
.field::placeholder {
  color: var(--text-2);
}

.fieldPill {
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-body);
  line-height: var(--lh-buddy-field);
}

.fieldPanel {
  min-height: 44px;
  font-size: var(--fs-button-sm);
}

/* The field's own box: the text area, and the dictation's mirror over it. */
.fieldWrap {
  position: relative;
  display: flex;
  min-width: 0;
}

/* The plan's language beside the mic (hero only): a fact, not a control. */
.language {
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-foot);
  color: var(--text-2);
  white-space: nowrap;
}

.multi .language {
  display: none;
}

/* The round controls: „+" and the mic, flat on the pill. */
.icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: var(--space-0);
  color: var(--text-2);
  cursor: default;
  background: transparent;
  border: 0;
  border-radius: var(--r-pill);
}

.iconDim {
  opacity: 0.45;
}

.iconLive {
  cursor: pointer;
}

.iconLive:hover:not(.iconDim) {
  color: var(--text);
  background: var(--hover-tint);
}

.iconLive:active:not(.iconDim) {
  background: var(--bg-chip);
}

.iconGlyph {
  width: 16px;
  height: 16px;
}

.send {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: var(--space-0);
  color: var(--on-accent-grad);
  cursor: pointer;
  background: var(--primary-fill);
  border: 0;
  border-radius: var(--radius-20px);
  box-shadow: none;
  transition-duration: var(--dur-fast);
  transition-property: transform;
  transition-timing-function: var(--ease);
}

.icon:focus-visible,
.send:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.send:hover:not([aria-disabled='true']) {
  transform: translateY(-1px);
}

.send:active:not([aria-disabled='true']) {
  transform: translateY(1px);
}

.stop {
  color: var(--text);
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge-focus);
  box-shadow: var(--shadow-buddy-control);
}

.sendOff {
  color: var(--text-3);
  cursor: default;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  box-shadow: none;
}

.sendGlyph {
  width: 17px;
  height: 17px;
}

/* The thin strip tucked under the pill: the owner's sentence and „Wrzuć arkusz" (board T), or a
 * dictation's one line. Its own box starts at the pill's foot and keeps its 44/42 px whatever it
 * says; the part tucked behind the pill is drawn by `::before`, so it costs no layout. */
.tray {
  position: relative;
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  margin: var(--space-0) var(--space-7);
  padding: var(--space-0) var(--space-2) var(--space-0) var(--space-18px);
  background: var(--buddy-tray);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-top: 0;
  border-radius: var(--space-0) var(--space-0) var(--radius-lg) var(--radius-lg);

  /* The hero's and the dock's tray carry a dictation's status too, and never change height for it
   * (board notes, „Sizes"): 44 px under the hero, 42 px under the dock, one line either way. */
  box-sizing: border-box;
  height: 44px;
}

.tray::before {
  position: absolute;
  inset: var(--space-minus-28px) var(--space-minus-1px) auto;
  height: 28px;
  content: '';
  background: var(--buddy-tray);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-bottom: 0;
}

.dock .tray {
  height: 42px;
}

.panel .tray {
  height: auto;
  margin: var(--space-0) var(--space-14px);
  padding: var(--space-11px) var(--space-18px);
}

.panel .tray::before {
  display: none;
}

.trayText {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-foot);
  color: var(--text-2);
}

.traySoon {
  flex: none;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-foot);
  color: var(--text-2);
}

/* „Wrzuć arkusz": the tray's one act (board T), a quiet pill with the sheet's glyph. */
.trayAction {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-foot);
  font-weight: var(--fw-medium);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: 0;
  border-radius: var(--r-pill);
}

.trayAction:hover {
  background: var(--hover-tint);
}

.trayAction:active {
  background: var(--bg-chip);
}

.trayAction:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.trayActionGlyph {
  width: 16px;
  height: 16px;
  color: var(--text-2);
}

/* The panel's keys and content language (board I's `.hint`). */
.hint {
  display: flex;
  justify-content: space-between;
  margin: var(--space-0);
  font-size: var(--fs-micro);
  color: var(--text-2);
}

/* „Wyślesz, gdy Buddy skończy.": a send refused while Buddy works, said once, quietly. */
.heldBack {
  margin: var(--space-0) var(--space-7);
  font-size: var(--fs-foot);
  color: var(--text-2);
}

/* ══ Dictation (`docs/design/ai-buddy-dictation-2026-10-13/`, redrawn by board „Buddy P5 · Stany",
 * the owner's version: a red microphone, the wave and a stop, which outranks the older board) ══
 * The recording lane is laid over the composer's own controls, so the box never changes size: it
 * spans the „+", the field, the language and the mic, which keep their room and only fade. Once
 * the text runs past one line the lane takes the control row under it and the field stays. */

/* The box keeps its 2px lift for the whole dictation: the trainer is composing. */
.lifted,
.lifted:focus-within {
  box-shadow: var(--shadow-buddy-composer-lifted);
  transform: translateY(-2px);
}

/* The pill's cells. Every child names its cell, because grid auto-placement would push the others
 * round a lane placed first. */
.cellSheet {
  grid-area: sheet;
}

.cellField {
  grid-area: field;
}

.cellLanguage {
  grid-area: language;
}

.cellMic {
  grid-area: mic;
}

.cellAction {
  grid-area: action;
}

.cellLane {
  grid-row: 1;
  grid-column: 1 / 5;
}

.multi .cellLane {
  grid-row: 2;
}

.lane {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-width: 0;
  height: 40px;
}

/* Entrances and exits of the lane and the idle controls (board notes, „Motion"). */
.fades {
  transition-duration: var(--dur-base);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease);
}

.away {
  pointer-events: none;
  opacity: 0;
  transform: translateY(3px);
  transition-duration: var(--dur-fast);
  transition-timing-function: var(--ease-exit);
}

/* The idle controls under the lane: hidden, not moved. */
.under {
  pointer-events: none;
  opacity: 0;
}

.timer {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;

  /* The lane ends at the mic's cell, a hairline from the round action: the timer keeps off it. */
  margin-right: var(--space-2);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  white-space: nowrap;
  transition-duration: var(--dur-slow);
  transition-property: color;
  transition-timing-function: var(--ease);
}

/* The last seconds: the one element carrying a verdict, beside the tray's words and the arc. */
.timerWarn {
  color: var(--buddy-warn);
}

.timerDim {
  color: var(--text-3);
}

/* „Słucham…" behind the red microphone, which sits a little closer to its word than the lane's gap. */
.listening {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-buddy-field);
  color: var(--text-2);
  white-space: nowrap;
  transition-duration: var(--dur-slow);
  transition-property: color;
  transition-timing-function: var(--ease);
}

.listeningDim {
  color: var(--text-3);
}

.listeningMic {
  flex: none;
  width: 18px;
  height: 18px;
  color: var(--text-3);
  transition-duration: var(--dur-slow);
  transition-property: color;
  transition-timing-function: var(--ease);
}

/* Red only while it records: the one state the colour names. A fill, never a glow. */
.listeningMicLive {
  color: var(--danger);
}

/* One continuous run filling the room between the word and the timer: the newest bar stands by the
 * timer and the history scrolls left, fading out before it reaches „Słucham…". */
.waveBox {
  position: relative;
  display: block;
  flex: 1;
  min-width: 0;
  height: 24px;
}

.wave {
  display: block;
  width: 100%;
  height: 100%;
}

/* The round action's glyphs, stacked: send or stop, the recording's red stop and the spinner
 * cross-fade in place. */
.actionGlyph {
  grid-area: 1 / 1;
  width: 17px;
  height: 17px;
  transition-duration: var(--dur-slow);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease-buddy-spring);
}

.actionGlyphOut {
  opacity: 0;
  transform: rotate(-70deg) scale(0.5);
}

.spin {
  color: var(--text-2);
  animation-name: spin;
  animation-duration: var(--dur-buddy-spin);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

/* The recording's stop: a red rounded square on the neutral raised round (the `.stop` face). */
.recordStop {
  width: 12px;
  height: 12px;
  background: var(--danger);
  border-radius: var(--radius-4px);
}

/* The stop while the browser asks and the spinner while the server works: neutral, not the accent. */
.neutral .recordStop {
  background: var(--text-3);
}

.neutral,
.neutral:hover {
  color: var(--text);
  cursor: default;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge-focus);
  box-shadow: var(--shadow-buddy-control);
  transform: none;
}

.actionWrap {
  position: relative;
  flex: none;
  width: 40px;
  height: 40px;
}

/* The countdown round the stop, 5px outside it, closing over the last ten seconds. */
.arc {
  position: absolute;
  inset: var(--space-minus-5px);
  width: 50px;
  height: 50px;
  pointer-events: none;
  opacity: 0;
  transform: rotate(-90deg);
  transition-duration: var(--dur-slow);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.arcOn {
  opacity: 1;
}

.arc circle {
  fill: none;
  stroke: var(--text-2);
  stroke-width: 1.25;
  stroke-linecap: round;
}

/* The mic and its answer: the glyph turns into a ✓ for a moment after the text lands. */
.micGlyph {
  grid-area: 1 / 1;
  width: 15px;
  height: 15px;
  transition-duration: var(--dur-base);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease-buddy-spring);
}

.micGlyphOut {
  opacity: 0;
  transform: scale(0.6);
}

.icon:active:not([aria-disabled='true'], .iconDim) {
  transform: translateY(1px);
}

/* The mic's word on hover and focus, and why it refuses when it does. */
.tipPositioner {
  z-index: 40;
}

.tip {
  padding: var(--space-7px) var(--space-10px);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  color: var(--text);
  white-space: nowrap;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge-focus);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-buddy-card);
  transition-duration: var(--dur-base);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease);
}

.tip[data-starting-style],
.tip[data-ending-style] {
  opacity: 0;
  transform: translateY(3px);
}

.tipKeys {
  display: inline-block;
  font-family: var(--font-body);
  margin-left: var(--space-2);
  padding: var(--space-1px) var(--space-5px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-5px);
}

.tipSub {
  display: block;
  margin-top: var(--space-2px);
  color: var(--text-2);
}

/* The field while a dictation stands over it: what the trainer wrote, read-only. */
.fieldListening::placeholder {
  color: var(--text-2);
}

/* The field's text veiled while the mirror draws the dictated words entering; the caret stays. */
.fieldVeiled {
  color: transparent;
  caret-color: var(--text);
}

.mirror {
  position: absolute;
  inset: var(--space-0);
  display: block;
  overflow: hidden;
  white-space: pre-wrap;
  pointer-events: none;
  overflow-wrap: break-word;
}

.mirrorText {
  display: block;
}

.mirrorHidden {
  color: transparent;
}

.marker {
  display: inline-block;
  width: 2px;
  height: 1.15em;
  margin: var(--space-0) var(--space-2px);
  vertical-align: -0.2em;
  background: var(--text-3);
  border-radius: var(--radius-1px);
  animation-name: marker;
  animation-duration: var(--dur-buddy-shimmer);
  animation-timing-function: var(--ease-in-out);
  animation-iteration-count: infinite;
}

.ghost {
  font-weight: var(--fw-medium);
}

/* The tray's line: the sheet offer, or the dictation's one line of status, in the same box. */
.trayLine {
  display: flex;
  flex: 1;
  gap: var(--space-10px);
  align-items: center;
  min-width: 0;
}

.trayGlyph {
  flex: none;
  width: 14px;
  height: 14px;
  color: var(--text-2);
}

.trayGlyphDanger {
  color: var(--danger-ink);
}

/* A dictation's sentence stays on its one line; where the width runs out it is cut, never wrapped. */
.trayOneLine {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trayStrong {
  color: var(--text);
}

.trayText kbd {
  font-family: inherit;
  font-weight: var(--fw-medium);
  color: var(--text);
}

@media (prefers-reduced-motion: reduce) {
  .marker,
  .spin {
    animation-name: none;
  }
}

@keyframes spin {
  to {
    transform: rotate(1turn);
  }
}

@keyframes marker {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}

/* A dictation's new line in the tray: in over 120 ms (board notes, „Motion"). */
.trayLineEnters {
  animation-name: tray-line-in;
  animation-duration: var(--dur-fast);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

@keyframes tray-line-in {
  from {
    opacity: 0;
  }
}
